import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import {
  formValueSelector, change, getFormValues, isValid,
} from 'redux-form';

import { cartItemsArrayPropTypes, userPropTypes } from 'helpers/propTypes';
import { isAbleToPurchase } from 'helpers/cart';
import { CART_CHECKOUT_DELIVERY_REQUESTED } from 'redux/constants/cart.constants';
import {
  checkout, submitCoupon, deleteCoupon, getProducts,
} from 'redux/actions/cart.actions';
import { getLocalStorage } from 'helpers/storage';
import {
  cartItemIdsSelector, cartItemsSelector, cartSizeSelector, cartSubtotalSelector,
  cartSubtotalTextilSelector,
} from 'redux/selectors/cart.selector';
import { getShippingText } from 'components/forms/PaymentForm/constants';
import { openSecureCheckoutModal } from 'redux/actions/modal.actions';
import { getCartQuote } from 'redux/actions/shipping.actions';
import { sendErrorNotification } from 'helpers/notifications';
import { openPaymentMethodsModal } from 'redux/actions/modal.actions';

import Checkout from 'views/Checkout';
import DeliveryForm from 'components/forms/DeliveryForm';
import PaymentForm from 'components/forms/PaymentForm';
import CheckoutTextil from 'views/Checkout/CheckoutTextil';
import ConfirmCart from 'components/commons/ConfirmCart';


const DeliveryContainer = (props) => {
  const {
    cartItems, cartSize, checkoutAction, submitCouponAction, deliveryMethod, items, loading,
    minimumPurchase, subtotal, user, coupon, deleteCouponAction, resetCouponField,
    openSecureCheckoutModalAction, showMPButton, getProductsAction, ids, transferInfo, envios,
    client, subtotalTextil, cartQuote, cartQuoteLoading, getCartQuoteAction, zipCode,
    cuotasMP, openPaymentMethodsModalAction, paymentMethods, hayAcuerdoConVendedor, termsAndConditions,
    deliveryFormValues, isDeliveryValid,
  } = props;
  const [deliveryFormData, setDeliveryFormData] = useState(null);
  const [couponText, setCouponText] = useState(null);
  const [emailComplete, setEmailComplete] = useState(false);
  const [emailCoupon, setEmailCoupon] = useState(null);
  const [initialized, setInitialized] = useState(false);
  const [isModalOpen, setModalOpen] = useState(false);
  const [pendingCheckout, setPendingCheckout] = useState(null);
  const canPay = !!deliveryFormData;

  const envio = envios.find(envio => envio.id.toString() === deliveryMethod);
  const showShippingQuote = envios?.some((e) => e.calculated_by_system) ?? false;
  const subTotEnv = deliveryMethod
    ? envio.monto_minimo !== 0 && subtotal >= envio.monto_minimo 
      ? 0
      : envio.calculated_by_system && cartQuote
        ? cartQuote.price
        : envio.monto
    : 0;

  useEffect(() => {
    if (coupon) deleteCouponAction();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!deliveryFormValues || !isDeliveryValid) {
      if (deliveryFormData) setDeliveryFormData(null);
      return;
    }

    if (showShippingQuote && zipCode !== deliveryFormValues.postal_code) {
      if (deliveryFormData) setDeliveryFormData(null);
      return;
    }

    const hasMinPurchase = client?.design === 3
      ? isAbleToPurchase(subtotalTextil, minimumPurchase)
      : isAbleToPurchase(subtotal, minimumPurchase);

    if (!hasMinPurchase) {
      if (deliveryFormData) setDeliveryFormData(null);
      return;
    }

    const nextData = { ...deliveryFormValues, coupon: coupon?.id };
    const prevDataJson = deliveryFormData ? JSON.stringify(deliveryFormData) : '';
    const nextDataJson = JSON.stringify(nextData);
    if (prevDataJson !== nextDataJson) setDeliveryFormData(nextData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isDeliveryValid,
    showShippingQuote,
    zipCode,
    subtotal,
    subtotalTextil,
    minimumPurchase,
    client?.design,
    coupon?.id,
    JSON.stringify(deliveryFormValues),
  ]);

  useEffect(() => {
    if (ids.length > 0 && !initialized) {
      getProductsAction(ids);
      setInitialized(true);
    }
    return () => getProductsAction([]);
    // eslint-disable-next-line
  }, [getProductsAction, JSON.stringify(ids)]);

  const onSubmitDeliveryForm = (formData) => {
    if (showShippingQuote && zipCode === formData.postal_code || !showShippingQuote) {
      if(client?.design === 3 && isAbleToPurchase(subtotalTextil, minimumPurchase)) {
        const data = { ...formData, coupon: coupon?.id };
        setDeliveryFormData(data);
      } else if (isAbleToPurchase(subtotal, minimumPurchase)) {
        const data = { ...formData, coupon: coupon?.id };
        setDeliveryFormData(data);
      }
    } else {
      sendErrorNotification('El código postal ingresado no coincide con el código postal de la dirección de envío');
    }
  };

  const onSubmitCoupon = () => {
    if (couponText && emailCoupon) {
      submitCouponAction(couponText, emailCoupon);
    }
  };

  const onDeleteCoupon = () => {
    deleteCouponAction();
    resetCouponField('');
    setCouponText('');
  };

  const validationEmailCoupon = (email) => {
    if (!email || !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(email)) {
      setEmailComplete(false);
      onDeleteCoupon();
    } else {
      setEmailComplete(true);
      setEmailCoupon(email);
    }
  };

  const renderDeliveryForm = () => (
    <DeliveryForm
      coupon={coupon}
      onSubmit={onSubmitDeliveryForm}
      user={user}
      onSubmitCoupon={coupon ? onDeleteCoupon : onSubmitCoupon}
      couponText={couponText}
      setCouponText={setCouponText}
      emailComplete={emailComplete}
      setEmailComplete={setEmailComplete}
      setEmailCoupon={setEmailCoupon}
      validationEmailCoupon={validationEmailCoupon}
      cartQuote={cartQuote}
      cartQuoteLoading={cartQuoteLoading}
      getCartQuoteAction={(zipCode) => getCartQuoteAction(zipCode, cartItems)}
      zipCode={zipCode}
      showShippingQuote={showShippingQuote}
    />
  );

  const onConfirmCheckout = () => {
    if (!pendingCheckout) return;
    const { finalData, checkoutItems } = pendingCheckout;
    setModalOpen(false);
    checkoutAction(
      CART_CHECKOUT_DELIVERY_REQUESTED,
      finalData,
      checkoutItems,
      user ? user.id : null,
    );
    deleteCouponAction();
    setPendingCheckout(null);
  };

  const onSubmitPaymentForm = (formData) => {
    if (!deliveryFormData) {
      sendErrorNotification('Completá tus datos de envío antes de continuar con el pago');
      return;
    }
    const isMP = formData.card || formData.token || formData.payment_method_id;

    const selectedMethod = paymentMethods.find(p => p.code === formData.payment_method);
    const methodPercent = selectedMethod ? parseFloat(selectedMethod.percent) : 0;

    const finalAmount = isMP ? getMPAmount() : Math.round(getBaseForPayment() * (1 - methodPercent) * 100) / 100;

    const finalData = {
      ...deliveryFormData,
      ...formData,
      transaction_amount: finalAmount,
      shipping_cost: subTotEnv,
    };

    if (isMP) finalData.payment_method = 'MP';

    const checkoutItems = client?.design === 3 ? items : cartItems;
    const canCheckout = client?.design === 3
      ? isAbleToPurchase(subtotalTextil, minimumPurchase)
      : isAbleToPurchase(subtotal, minimumPurchase);

    if (canCheckout) {
      setPendingCheckout({ finalData, checkoutItems });
      setModalOpen(true);
      window.scrollTo(0, 0);
    }
  };

  const getBaseForPayment = (selectedMethodCode) => {
    const methodObj = paymentMethods.find(p => p.code === selectedMethodCode);
    const allowsAutoDiscount = methodObj ? methodObj.allow_discount : false;

    const discountFromStorage = getLocalStorage('discount');
    const currentSubtotal = client?.design === 3 ? subtotalTextil : subtotal;
    
    const baseValue = (allowsAutoDiscount && discountFromStorage !== null) 
        ? parseFloat(discountFromStorage) 
        : currentSubtotal;

    const couponAmount = coupon?.percentage
        ? (coupon.percentage / 100) * currentSubtotal
        : (coupon?.amount || 0);

    return Math.max(0, baseValue - couponAmount);
  };

  const getMPAmount = () => {
    const mpMethod = paymentMethods.find(p => p.code === 'MP' || p.name.toLowerCase().includes('mercado pago'));
    const discountPercent = mpMethod ? parseFloat(mpMethod.percent) : 0;
    
    const base = getBaseForPayment();
    return Math.round(base * (1 - discountPercent) * 100) / 100;
  };

  const onSecureCheckoutClick = (formData) => {
    if (!deliveryFormData) {
      sendErrorNotification('Completá tus datos de envío antes de continuar con el pago');
      return;
    }
    const mpMethod = paymentMethods.find(p => p.code === 'MP' || p.name.toLowerCase().includes('mercado pago'));
    const discountPercent = mpMethod ? parseFloat(mpMethod.percent) : 0;
    const base = getBaseForPayment('MP');
    const finalAmount = Math.round(base * (1 - discountPercent) * 100) / 100;

    const updatedFormData = {
      ...formData,
      transaction_amount: finalAmount,
      payment_method: 'MP'
    };
    openSecureCheckoutModalAction({
      callback: onSubmitPaymentForm,
      email: deliveryFormData.email,
      formData: updatedFormData,
      total: finalAmount,
    });
  };

  const renderPaymentForm = () => (
    <PaymentForm
      showMPButton={showMPButton}
      deliveryMethod={deliveryMethod}
      onSubmit={onSubmitPaymentForm}
      onSecureCheckoutClick={onSecureCheckoutClick}
      transferInfo={transferInfo}
      envios={envios}
      hayAcuerdoConVendedor={hayAcuerdoConVendedor}
      canPay={canPay}
      termsAndConditions={termsAndConditions}
    />
  );

  const renderUnifiedForm = () => (
    <>
      {renderDeliveryForm()}
      {renderPaymentForm()}
    </>
  );

  const handleOpenPaymentModal = () => {
    const finalPrice = subtotalTextil;
    openPaymentMethodsModalAction(cuotasMP, finalPrice);
  };

  const renderWithConfirmCart = (checkoutNode) => (
    <>
      {checkoutNode}
      <ConfirmCart
        isOpen={isModalOpen}
        onClose={() => setModalOpen(false)}
        onConfirm={onConfirmCheckout}
      />
    </>
  );

  switch(client?.design) {
    case 3:
      return renderWithConfirmCart(
        <CheckoutTextil
          coupon={coupon}
          cartSize={cartSize}
          items={items}
          loading={loading}
          minimumPurchase={minimumPurchase}
          renderForm={renderUnifiedForm}
          shippingText={getShippingText(deliveryMethod, subtotal, envios, cartQuote)}
          subtotal={subtotalTextil}
          subTotEnv={subTotEnv}
          url="/envio-a-domicilio"
          urlText="Envío a domicilio"
          onOpenPaymentModal= {handleOpenPaymentModal}
          cuotasMP={cuotasMP}
          showMPButton={showMPButton}
          paymentMethods={paymentMethods}
        />,
      );
    default:
      return renderWithConfirmCart(
        <Checkout
          coupon={coupon}
          cartSize={cartSize}
          items={items}
          loading={loading}
          minimumPurchase={minimumPurchase}
          renderForm={renderUnifiedForm}
          shippingText={getShippingText(deliveryMethod, subtotal, envios, cartQuote)}
          subtotal={subtotal}
          subTotEnv={subTotEnv}
          url="/envio-a-domicilio"
          urlText="Envío a domicilio"
          onOpenPaymentModal= {handleOpenPaymentModal}
          cuotasMP={cuotasMP}
          showMPButton={showMPButton}
          client={client}
          paymentMethods={paymentMethods}
        />,
      );
  }
};

const selector = formValueSelector('payment');
const mapStateToProps = (state) => ({
  coupon: state.cart.coupon,
  cartItems: state.cart.items,
  cartSize: cartSizeSelector(state),
  deliveryMethod: selector(state, 'delivery_method'),
  ids: cartItemIdsSelector(state),
  items: cartItemsSelector(state),
  loading: state.cart.loading,
  minimumPurchase: state.app.minimumPurchase,
  subtotal: cartSubtotalSelector(state),
  showMPButton: state.app.showMPButton,
  transferInfo: state.app.transferInfo,
  user: state.user.data,
  envios: state.app.envios,
  client: state.app.client,
  subtotalTextil: cartSubtotalTextilSelector(state, true),
  cartQuote: state.shipping.cartQuote,
  cartQuoteLoading: state.shipping.loading,
  zipCode: state.shipping.zipCode,
  cuotasMP: state.app.cuotasMP,
  paymentMethods: state.app.paymentMethods,
  hayAcuerdoConVendedor: state.app.hayAcuerdoConVendedor,
  termsAndConditions: state.app.termsAndConditions,
  deliveryFormValues: getFormValues('delivery')(state),
  isDeliveryValid: isValid('delivery')(state),
});

const mapDispatchToProps = (dispatch) => ({
  checkoutAction: bindActionCreators(checkout, dispatch),
  submitCouponAction: bindActionCreators(submitCoupon, dispatch),
  deleteCouponAction: bindActionCreators(deleteCoupon, dispatch),
  resetCouponField: (value) => dispatch(change('delivery', 'coupon', value)),
  openSecureCheckoutModalAction: bindActionCreators(openSecureCheckoutModal, dispatch),
  getProductsAction: bindActionCreators(getProducts, dispatch),
  getCartQuoteAction: bindActionCreators(getCartQuote, dispatch),
  openPaymentMethodsModalAction: bindActionCreators(openPaymentMethodsModal, dispatch),
});

DeliveryContainer.defaultProps = {
  deliveryMethod: null,
  user: null,
  coupon: null,
  zipCode: '',
};

DeliveryContainer.propTypes = {
  cartItems: cartItemsArrayPropTypes.isRequired,
  cartSize: PropTypes.number.isRequired,
  coupon: PropTypes.shape({
    id: PropTypes.number.isRequired,
    percentage: PropTypes.number.isRequired,
    amount: PropTypes.number.isRequired,
  }),
  getProductsAction: PropTypes.func.isRequired,
  ids: PropTypes.arrayOf(PropTypes.number).isRequired,
  submitCouponAction: PropTypes.func.isRequired,
  deleteCouponAction: PropTypes.func.isRequired,
  resetCouponField: PropTypes.func.isRequired,
  openSecureCheckoutModalAction: PropTypes.func.isRequired,
  checkoutAction: PropTypes.func.isRequired,
  deliveryMethod: PropTypes.string,
  items: cartItemsArrayPropTypes.isRequired,
  loading: PropTypes.bool.isRequired,
  minimumPurchase: PropTypes.number.isRequired,
  subtotal: PropTypes.number.isRequired,
  showMPButton: PropTypes.bool.isRequired,
  user: userPropTypes,
  cartQuote: PropTypes.object,
  cartQuoteLoading: PropTypes.bool.isRequired,
  getCartQuoteAction: PropTypes.func.isRequired,
  zipCode: PropTypes.string,
  deliveryFormValues: PropTypes.object,
  isDeliveryValid: PropTypes.bool,
};

export default connect(mapStateToProps, mapDispatchToProps)(DeliveryContainer);
